// components/portal/Table.tsx
'use client';

import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTableColumnSizing } from './useTableColumns';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { TableGlassProvider, TABLE_GLASS_CONTAINER, TABLE_GLASS_HEADER } from './tableSurface';
import { createPortal } from 'react-dom';
import { PageCrop } from './PageCrop';
import { User, Hash as HashIcon, Calendar, Loader, AlignLeft, type LucideIcon } from 'lucide-react';
import {
  EntityBulkAction,
  EntityRowAction,
  EntityType,
  InventoryEntityState,
  InventoryEntityType,
  InventoryRegistry,
  QuickMenuAction,
  InventoryStatsEntityType,
  InventoryStatsEntry,
  INVENTORY_STATS_GLOBAL_SCOPE_KEY,
} from '#types/types';
import { ViewSection } from '@/components/portal/ViewSection';
import { resolveRecordValue, useInventoryList } from './useInventoryList';
import { useFlipRows } from './useFlipRows';
import { InventoryListToolbar } from './InventoryListToolbar';
import { buildCsvContent, downloadCsv } from '@/lib/portal/csvExport';
import { InventoryMetricsBar } from './InventoryMetricsBar';
import { Type, Hash, Users, MapPin, Gauge, ArrowUpDown, SlidersHorizontal, BarChart3, Eye, EyeOff, X } from '@/components/ui/lucide-memo';
import { AppTooltip } from '@/components/ui/tooltip';
import clsx from 'clsx';
import styles from './Table.module.css';
import { TableSelectionCheckbox } from './TableSelectionCheckbox';
import { TableStatsPanel } from './TableStatsPanel';
import {
  getSessionTableFocusPageTitle,
  getSessionTableFocusViewForEntityType,
  isTableFocusSubject,
  resolvePortalViewFromTableFocus,
  selectActiveSubjectFromInventoryState,
} from '#lib/leasing/session-focus';
import { HostOwnsListNavigationContext, HostRowOpenContext } from './tableChromeContext';
import { useTableFocusRuntimePublication } from './useTableFocusRuntimePublication';

interface TableSortOption<K extends InventoryEntityType> {
  label: string;
  field: string;
  localAccessor?: (item: InventoryEntityState<K>) => unknown;
}

export interface TableFilterOption<K extends InventoryEntityType> {
  key: string;
  label: string;
  icon?: React.ReactNode;
  localAccessor?: (item: InventoryEntityState<K>) => unknown;
  /** Predefined values → a Notion-style value picker in the filter popover. */
  values?: Array<{ value: string; label: string }>;
}

type TableColumnType = 'text' | 'select' | 'person' | 'number' | 'date' | 'longtext';

// How far the opened cell bleeds past its own edges, and the narrowest it will open at.
const CELL_CARD_BLEED = 6;
const CELL_CARD_MIN_WIDTH = 260;

export interface TableColumnDef {
  id: string;
  header: React.ReactNode;
  minWidth: number;
  maxWidth?: number;
  align?: 'left' | 'center' | 'right';
  /** Notion-style column type, drives the header icon. Inferred from id when unset. */
  type?: TableColumnType;
  /** Off until the user shows it from the column picker (or the table's own expand handle). */
  defaultHidden?: boolean;
  /** The column's name in the column picker, when `header` is not plain text. */
  label?: string;
  /**
   * The small gray icon before the header label, chosen for what the column holds when the
   * table is built. Every column of every table declares one (a source contract enforces it):
   * the old "Aa" glyph told the reader nothing, and an inferred icon is a guess about a
   * column name, not a decision about its content.
   */
  icon?: LucideIcon;
}

/** Best-effort column type for the Notion-style header icon (id + alignment). */
function inferColumnType(column: TableColumnDef): TableColumnType | null {
  const k = column.id.toLowerCase();
  if (k === 'actions' || k === '_selection' || k === '') return null;
  if (/(status|stage|state|priority|urgency|category|kind|channel|response)/.test(k) || k === 'type') return 'select';
  if (/(occupant|person|manager|owner|contact|tenant|assignee|member)/.test(k)) return 'person';
  if (
    column.align === 'right' ||
    /(surface|area|rent|score|size|count|price|amount|value|rate|sqm|sqft|popularity|allocation|units|occupanc|vacan|gla)/.test(k)
  )
    return 'number';
  if (/(date|updated|created|deadline)/.test(k)) return 'date';
  if (/(description|notes|info|comment|rationale|details)/.test(k)) return 'longtext';
  return 'text';
}

/**
 * Header label. Plain words, as ChatGPT titles a column (owner, 2026-09-28): the Notion type icons
 * before each title read as clutter once the rows themselves went quiet. Glass tables only.
 */
function ColumnHeaderLabel({ column, plain = false }: { column: TableColumnDef; plain?: boolean }) {
  const colType = column.type ?? inferColumnType(column);
  if (!colType) return <>{column.header}</>;
  if (plain) return <span className="min-w-0 truncate">{column.header}</span>;
  let icon: React.ReactNode;
  if (column.icon) {
    icon = <column.icon size={13} strokeWidth={1.8} className="shrink-0 text-[#6E6E6E]" />;
  } else if (colType === 'text') {
    // A plain label beats a glyph that means nothing.
    return <span className="truncate">{column.header}</span>;
  } else {
    const Icon =
      colType === 'select'
        ? Loader
        : colType === 'person'
          ? User
          : colType === 'number'
            ? HashIcon
            : colType === 'date'
              ? Calendar
              : AlignLeft;
    icon = <Icon size={13} strokeWidth={1.8} className="shrink-0 text-[#6E6E6E]" />;
  }
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {icon}
      <span className="truncate">{column.header}</span>
    </span>
  );
}

export interface TableRowSelectionContext {
  isSelectable: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onSelectionZoneMouseDown: (event: React.MouseEvent, id: string) => void;
}

export interface TableContextMenuAction {
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
}

type TableViewFilters<K extends InventoryEntityType> =
  Partial<InventoryRegistry[K]['filters']> &
  Record<string, unknown> & {
    search?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    organisationId?: string;
  };

interface TableRowWrapperProps<TItem extends { id: string }> {
  item: TItem;
  renderRow: (
    item: TItem,
    isExpanded: boolean,
    toggle: () => void,
    columns?: TableColumnDef[],
    selectionContext?: TableRowSelectionContext,
  ) => React.ReactNode;
  columns?: TableColumnDef[];
  isExpanded: boolean;
  onToggle: (id: string) => void;
  /** Selection is passed field by field, NOT as a ready-made object: a literal built in the
   *  parent's .map() is a new reference on every render and defeats this component's memo for
   *  every row at once. The context object is reassembled here, behind stable primitives. */
  isSelectable: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onSelectionZoneMouseDown: (event: React.MouseEvent, id: string) => void;
  onContextMenu?: (event: React.MouseEvent, item: TItem) => void;
  /** Set only when a host owns row opening; undefined leaves the row click entirely to the row. */
  onOpen?: (id: string) => void;
}

function TableRowWrapperInner<TItem extends { id: string }>({
  item,
  renderRow,
  columns,
  isExpanded,
  onToggle,
  isSelectable,
  isSelected,
  onToggleSelect,
  onSelectionZoneMouseDown,
  onContextMenu,
  onOpen,
}: TableRowWrapperProps<TItem>) {
  const toggle = useCallback(() => onToggle(item.id), [onToggle, item.id]);
  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => onContextMenu?.(event, item),
    [onContextMenu, item],
  );
  const selectionContext = useMemo<TableRowSelectionContext>(
    () => ({ isSelectable, isSelected, onToggleSelect, onSelectionZoneMouseDown }),
    [isSelectable, isSelected, onToggleSelect, onSelectionZoneMouseDown],
  );
  // Caught here rather than passed as an onClick into each of the fifteen renderRow callbacks:
  // half of them wire none (a Property row has no click handler at all), and the ones that do
  // wire their own meaning. Controls inside the row keep their click.
  const handleOpen = useCallback((event: React.MouseEvent) => {
    if (!onOpen) return;
    if ((event.target as HTMLElement | null)?.closest('button, a, input, select, textarea, [role="menu"]')) return;
    onOpen(item.id);
  }, [onOpen, item.id]);
  return (
    <div data-table-row-id={item.id} onContextMenu={handleContextMenu} onClick={onOpen ? handleOpen : undefined}>
      {renderRow(item, isExpanded, toggle, columns, selectionContext)}
    </div>
  );
}

const TableRowWrapper = memo(TableRowWrapperInner) as typeof TableRowWrapperInner & { displayName?: string };
TableRowWrapper.displayName = 'TableRowWrapper';

interface TableProps<K extends InventoryEntityType> {
  entityType: K;
  pageSize?: number;
  /** Hide the toolbar search when the caller already renders one above the table. */
  hideSearch?: boolean;
  /** Compact preview mode: renders just the first rows with no toolbar, no metrics
   *  bar, no ledger header, and inline (non-scrolling) height. Used by the Source
   *  dashboard preview sections. */
  preview?: boolean;
  initialFilters?: Partial<InventoryRegistry[K]['filters']>;
  lockedFilters?: Partial<InventoryRegistry[K]['filters']>;
  /** Client-side row gate applied AFTER querying. Server-side lockedFilters alone can be
   *  bypassed by live socket upserts / stale query caches (a running campaign's PENDING
   *  seeds leaking into a status-locked view), this predicate is the belt-and-braces. */
  clientRowFilter?: (item: InventoryEntityState<K>) => boolean;
  /** Replaces the default "No results found." body when the (filtered) view is empty ,
   *  e.g. a running campaign's "the reviewer is working" state instead of a dead end. */
  emptyStateContent?: React.ReactNode;
  /**
   * Rendered after the last row, inside the scrolling row list. For rows that are COMING - the
   * sourcing pending-brand strip - so they queue behind the real ones and scroll with them
   * instead of being stacked under the table by the caller. Ignored while the view is empty,
   * where emptyStateContent is the surface.
   *
   * The function form receives the SAME resolved columns the rows get (hidden ones dropped,
   * selection column prepended), so a placeholder row can be laid out on the real grid instead of
   * carrying a second, drifting set of widths.
   */
  afterRowsContent?: React.ReactNode | ((columns?: TableColumnDef[]) => React.ReactNode);
  /**
   * Re-rank the loaded rows client-side on the active NUMERIC sort field. The server decides
   * WHICH rows are loaded; this decides the order they are shown in, so a live re-score (rubric
   * re-apply, re-judge) visibly moves rows the instant their new score lands instead of waiting
   * for the next query. Inert on non-numeric sorts, and identical to the server order until a
   * score actually changes. See the comparator for the nulls-last / stable-tiebreak contract.
   */
  liveResort?: boolean;
  /**
   * True while a re-score the user just triggered is reworking these rows. Drives the sweep over
   * the list, so the table reads as being actively edited rather than merely loading.
   */
  rescoreActive?: boolean;
  columns?: TableColumnDef[];
  gridClass?: string;
  glass?: boolean;
  sheet?: boolean;
  sortOptions: TableSortOption<K>[];
  scopedIds?: string[] | null;
  additionalSearchText?: Readonly<Record<string, string>>;
  disableManualRefresh?: boolean;
  onManualRefresh?: () => void | Promise<void>;
  /** When true, periodically re-query so a live view (e.g. a running campaign) keeps filling as rows arrive. */
  liveRefresh?: boolean;
  /**
   * Editor mode: clicking a row expands its detail inline (table grows
   * downward) instead of navigating to the entity page. Default off, every
   * other table keeps the navigate-on-click behaviour.
   */
  inlineExpand?: boolean;
  /** Notifies the parent which row is currently expanded (null when none). */
  onExpandedIdChange?: (id: string | null) => void;
  /**
   * Controls the expanded row. Omit to let the table own it (every existing caller); pass it,
   * even as null, and the parent's value wins, for callers whose store already holds the
   * focused row and can clear it from elsewhere.
   */
  expandedId?: string | null;
  filterOptions?: TableFilterOption<K>[];
  renderTableHeader?: () => React.ReactNode;
  renderRow: (
    item: InventoryEntityState<K>,
    isExpanded: boolean,
    toggle: () => void,
    columns?: TableColumnDef[],
    selectionContext?: TableRowSelectionContext,
  ) => React.ReactNode;
  renderToolbarExtras?: () => React.ReactNode;
  /** No toolbar row at all (search, filters, columns): a list embedded in a card that needs none. */
  hideToolbar?: boolean;
  toolbarContextMenuActions?: TableContextMenuAction[];
  assetLedgerTitle?: string;
  /** The page heading, even inside a host that owns navigation (Slides Training). */
  pageTitle?: string;
  assetLedgerBreadcrumb?: string;
  onVisibleItemIdsChange?: (ids: string[]) => void;
  /** Leading toolbar slot, before the create button: the same slot Grid exposes, so a dashboard switch sits in the same place on both. */
  beforeCreateContent?: React.ReactNode;
  onCreateClick?: () => void;
  createButtonLabel?: string;
  bulkActions?: EntityBulkAction<InventoryEntityState<K>>[];
  rowActions?: EntityRowAction<InventoryEntityState<K>>[];
  enableSelection?: boolean;
  controlledSelectionIds?: string[];
  onControlledSelectionChange?: (ids: string[]) => void;
  supportsStats?: boolean;
  statsEntityType?: InventoryStatsEntityType;
  forceGlobalStats?: boolean;
  renderStatsView?: (
    statsEntry: InventoryStatsEntry | null,
    context: {
      onRetry: () => void;
      entityType: InventoryEntityType;
      scopeKey: string;
    }
  ) => React.ReactNode;
}

function TableInner<K extends InventoryEntityType>({
  entityType,
  initialFilters,
  lockedFilters,
  hideSearch = false,
  clientRowFilter,
  emptyStateContent,
  afterRowsContent,
  liveResort = false,
  rescoreActive = false,
  columns,
  gridClass,
  glass,
  sheet = false,
  sortOptions,
  pageSize = 60,
  liveRefresh,
  renderTableHeader,
  renderRow,
  renderToolbarExtras,
  hideToolbar = false,
  toolbarContextMenuActions,
  assetLedgerTitle,
  assetLedgerBreadcrumb,
  pageTitle: explicitPageTitle,
  scopedIds,
  additionalSearchText,
  preview = false,
  disableManualRefresh,
  onManualRefresh,
  inlineExpand = false,
  onExpandedIdChange,
  expandedId: controlledExpandedId,
  filterOptions,
  onVisibleItemIdsChange,
  beforeCreateContent,
  onCreateClick,
  createButtonLabel,
  bulkActions = [],
  rowActions = [],
  enableSelection = false,
  controlledSelectionIds,
  onControlledSelectionChange,
  supportsStats = false,
  statsEntityType,
  forceGlobalStats = false,
  renderStatsView,
}: TableProps<K>) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const userContext = useKernelStore((state) => state.userContext);
  const activeSubject = useKernelStore(selectActiveSubjectFromInventoryState);
  const {
    viewItems: rawViewItems,
    viewIsLoading,
    viewError,
    refreshIsLoading,
    viewPagination,
    viewFilters,
    hasLockedFilters,
    isFilterKeyLocked,
    mergeWithLockedFilters,
    setFilters,
    handleSearchChange,
    handleSortChange,
    handleManualRefresh,
    handleReachBottom,
  } = useInventoryList({
    entityType,
    pageSize: preview ? 6 : pageSize,
    initialFilters,
    lockedFilters,
    scopedIds,
    additionalSearchText,
    sortOptions,
    filterOptions,
    disableManualRefresh,
    onManualRefresh,
    liveRefresh
  });
  // Client-side gate on top of the server query, see the clientRowFilter prop doc.
  const gatedViewItems = useMemo(
    () => (clientRowFilter ? rawViewItems.filter((item) => clientRowFilter(item)) : rawViewItems),
    [rawViewItems, clientRowFilter],
  );

  // Live re-rank, see the liveResort prop doc. The server order is the baseline and the
  // tiebreak, so with untouched scores this produces the EXACT order the query returned
  // (the API sorts scores desc with nulls last, and falls back to updatedAt); it only
  // diverges once a socket update rewrites a score under the user's eyes.
  const viewItems = useMemo(() => {
    if (!liveResort) return gatedViewItems;
    const sortBy = typeof (viewFilters as Record<string, unknown>).sortBy === 'string'
      ? ((viewFilters as Record<string, unknown>).sortBy as string)
      : '';
    if (!sortBy) return gatedViewItems;
    const directionMultiplier = (viewFilters as Record<string, unknown>).sortDir === 'asc' ? 1 : -1;
    // The sort option's own reading wins: a lead queued for a new pass still carries its last
    // run's fitScore, and ranking on that put unscored brands above the ones just scored.
    const accessor = sortOptions.find((option) => option.field === sortBy)?.localAccessor;
    const toRankValue = (item: InventoryEntityState<K>): number | null => {
      const raw = accessor ? accessor(item) : resolveRecordValue(item as unknown as Record<string, unknown>, sortBy);
      return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    };
    // A row the user (or the judge) has set aside sinks below every live row, whatever it
    // scored. A brand rejected for cannibalisation can still score 80, so on a plain score
    // sort it lands near the top and reads as a recommendation. Sorting on the status string
    // cannot express this: it is a free-form column with no ordinal.
    const isSetAside = (item: InventoryEntityState<K>): boolean => {
      const raw = resolveRecordValue(item as unknown as Record<string, unknown>, 'status');
      const status = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
      return status === 'DISMISSED' || status === 'INVALIDATED' || status === 'DISCARDED';
    };
    // Non-numeric sort fields (status, dates, names) resolve to null everywhere, which makes
    // the comparator fall through to the server index: re-ranking is strictly a scores feature.
    return gatedViewItems
      .map((item, index) => ({ item, index, rank: toRankValue(item), setAside: isSetAside(item), inFlight: resolveRecordValue(item as unknown as Record<string, unknown>, 'status') === 'RUNNING' }))
      .sort((left, right) => {
        if (left.setAside !== right.setAside) return left.setAside ? 1 : -1;
        if (left.rank === null || right.rank === null) {
          // Unranked: the brands being scored right now sit just under the ranked ones, the queue below.
          if (left.rank === right.rank) return left.inFlight !== right.inFlight ? (left.inFlight ? -1 : 1) : left.index - right.index;
          return left.rank === null ? 1 : -1;
        }
        if (left.rank !== right.rank) return (left.rank - right.rank) * directionMultiplier;
        return left.index - right.index;
      })
      .map((entry) => entry.item);
  }, [gatedViewItems, liveResort, sortOptions, viewFilters]);

  const [uncontrolledSelectedIds, setUncontrolledSelectedIds] = useState<Set<string>>(new Set());
  const isSelectionControlled = Boolean(onControlledSelectionChange) && Array.isArray(controlledSelectionIds);
  const selectedIds = useMemo(() => {
    if (!isSelectionControlled || !Array.isArray(controlledSelectionIds)) {
      return uncontrolledSelectedIds;
    }
    return new Set(
      controlledSelectionIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    );
  }, [controlledSelectionIds, isSelectionControlled, uncontrolledSelectedIds]);
  const [viewMode, setViewMode] = useState<'rows' | 'stats'>('rows');

  const { widthsRef: columnWidthsRef, widthVersion, startResize: startColumnDrag, autoSizeAt } = useTableColumnSizing();
  // What the user chose per column; a column never touched follows its own `defaultHidden`.
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnsQuery, setColumnsQuery] = useState('');
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  // The menu is portalled and pinned to its button: rendered inside the table it was clipped by the
  // scroll area whenever the list was short (owner, 2026-09-28).
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState<{ left: number; top: number } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const typedViewFilters = viewFilters as TableViewFilters<K>;
  const visibleItemIds = useMemo(() => viewItems.map((item) => item.id), [viewItems]);
  const visibleItemIdsSignature = useMemo(() => visibleItemIds.join(','), [visibleItemIds]);
  const tableFocusView = useMemo(
    () => getSessionTableFocusViewForEntityType(entityType as any),
    [entityType]
  );
  useTableFocusRuntimePublication({
    entityType,
    presentationMode: 'table',
    viewFilters,
    pageSize,
    visibleItemIds,
    enabled: !hasLockedFilters && !preview,
  });
  const isCurrentSessionTableFocus = useMemo(() => {
    if (!tableFocusView) return false;
    if (!isTableFocusSubject(activeSubject)) return false;
    const activeTableView = resolvePortalViewFromTableFocus(activeSubject);
    return activeTableView === tableFocusView;
  }, [activeSubject, tableFocusView]);
  const hostOwnsNavigation = useContext(HostOwnsListNavigationContext);
  // Double-click grows the cell in place, the way a Notion cell opens: a card over the cell's
  // own footprint, same left edge and width, tall enough for the whole value, read off the
  // cell's text so no row renderer has to know. A transparent catcher behind it closes it, as
  // do page scroll, resize and Escape.
  const [cellViewer, setCellViewer] = useState<{ text: string; top: number; left: number; width: number; maxHeight: number;
    crop?: { src: string; pageWidth: number; pageHeight: number; x: number; y: number } } | null>(null);
  const handleCellDoubleClick = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    // An editable cell edits on double click; lifting a read-only copy over it hid the editor.
    if (target.closest('button, a, input, textarea, select, [role="menuitem"], [data-cell-editable]')) return;
    const cell = target.closest<HTMLElement>(`.${styles.cell}`);
    if (!cell?.parentElement) return;
    const editable = cell.querySelector<HTMLElement>('[data-cell-editable]');
    if (editable) { event.preventDefault(); editable.click(); return; }
    // A renderer that shows less than it knows (flags plus a +N) declares the whole value.
    const text = (cell.querySelector<HTMLElement>('[data-cell-text]')?.dataset.cellText ?? cell.innerText).trim();
    const cropNode = cell.querySelector<HTMLElement>('[data-crop-src]');
    const crop = cropNode ? (() => {
      const numbers = (value?: string) => (value ?? '').split(',').map(Number);
      const [pageWidth = NaN, pageHeight = NaN] = numbers(cropNode.dataset.cropPage);
      const [x = NaN, y = NaN] = numbers(cropNode.dataset.cropCentre);
      return [pageWidth, pageHeight, x, y].every(Number.isFinite) ? { src: cropNode.dataset.cropSrc ?? '', pageWidth, pageHeight, x, y } : undefined;
    })() : undefined;
    if (!text && !crop) return;
    const r = cell.getBoundingClientRect();
    // Bleeds a few pixels past the cell so it reads as the cell lifting, not a box beside it;
    // narrow columns get a readable minimum and everything stays inside the viewport.
    const width = Math.min(Math.max(r.width + CELL_CARD_BLEED * 2, CELL_CARD_MIN_WIDTH), window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left - CELL_CARD_BLEED, window.innerWidth - width - 8));
    const top = Math.max(8, r.top - CELL_CARD_BLEED);
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    setCellViewer({ text, top, left, width, maxHeight: window.innerHeight - top - 8, crop });
  }, []);
  const cellCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!cellViewer) return;
    // A wheel tick inside the card scrolls the value; only the page moving under it closes it.
    const close = (event?: Event) => { if (event && cellCardRef.current?.contains(event.target as Node)) return; setCellViewer(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [cellViewer]);
  const hostRowOpen = useContext(HostRowOpenContext);
  const pageTitle = explicitPageTitle ?? (!hasLockedFilters && !preview && !hostOwnsNavigation
    ? (assetLedgerTitle || (isCurrentSessionTableFocus && tableFocusView ? getSessionTableFocusPageTitle(tableFocusView, lang === 'fr' ? 'fr' : 'en') : undefined))
    : undefined);

  useEffect(() => {
    if (!columnsMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-table-cols-menu]')) return;
      setColumnsMenuOpen(false);
    };
    // A pinned menu would drift off its button when anything scrolls, so scrolling closes it.
    const onScroll = (e: Event) => {
      if ((e.target as HTMLElement | null)?.closest?.('[data-table-cols-menu]')) return;
      setColumnsMenuOpen(false);
    };
    const timer = window.setTimeout(() => document.addEventListener('mousedown', onDown), 50);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [columnsMenuOpen]);

  // Expose the table's visible (client) width as a CSS var. An expanded full-width detail row reads
  // it to size itself to the viewport and stay put (position: sticky) instead of scrolling under the
  // chat panel when the columns overflow horizontally.
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => el.style.setProperty('--table-viewport-width', `${el.clientWidth}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dragSelectionRef = useRef<{
    anchorId: string;
    currentId: string;
    baselineSelectedIds: Set<string>;
    mode: 'add' | 'remove';
    didMove: boolean;
    scrollContainer: HTMLElement | null;
    autoScrollRaf: number | null;
    autoScrollSpeed: number;
    lastClientX: number;
    lastClientY: number;
  } | null>(null);
  const dragMouseMoveHandlerRef = useRef<((event: MouseEvent) => void) | null>(null);
  const dragMouseUpHandlerRef = useRef<((event: MouseEvent) => void) | null>(null);
  const suppressNextToggleRef = useRef(false);

  useEffect(() => { if (onVisibleItemIdsChange) onVisibleItemIdsChange(visibleItemIds); }, [visibleItemIdsSignature, onVisibleItemIdsChange]);
  const [uncontrolledExpandedId, setUncontrolledExpandedId] = useState<string | null>(null);
  // When the caller passes `expandedId`, IT owns the expansion and this table follows. The
  // Source shortlist needs that: the composer's subject chip clears the focused row from the
  // store, and with the expansion held only here the row stayed open while the store said
  // nothing was focused, so the next click on that same row computed "collapse" and the user
  // had to click twice to get back. One source of truth removes the whole desync class.
  const isExpansionControlled = controlledExpandedId !== undefined;
  const expandedId = isExpansionControlled ? controlledExpandedId : uncontrolledExpandedId;
  const setExpandedId = useCallback((next: string | null) => {
    if (!isExpansionControlled) setUncontrolledExpandedId(next);
  }, [isExpansionControlled]);
  // Mirror the expanded id in a ref so the toggle handler can compute the next value
  // WITHOUT a functional setState updater. onExpandedIdChange typically calls a parent
  // store setState (e.g. SourcingCenterView.setFocusedFit -> kernel.setState); running it
  // inside the setExpandedId updater fired it during TableInner's render phase, which React
  // flags as "Cannot update a component while rendering a different component". Keeping it in
  // the event handler makes it a normal event-driven update.
  const expandedIdRef = useRef<string | null>(null);
  useEffect(() => { expandedIdRef.current = expandedId; }, [expandedId]);

  // FLIP the body rows so a re-rank is seen as movement. The signature is the only thing that
  // triggers a measure pass: the rendered order plus the expanded row, i.e. exactly the two
  // things that can shift a row vertically. Plain data re-renders (a score changing in place,
  // a brand hydrating) never pay for it.
  const rowsContainerRef = useRef<HTMLDivElement>(null);
  useFlipRows(rowsContainerRef, `${visibleItemIdsSignature}#${expandedId ?? ''}`);
  const handleToggleRow = useCallback((id: string) => {
    if (!id) return;
    // A host owning row opening wins over both branches, expansion included: unfolding an entity
    // page inside a raw database browser is the app leaking back into it.
    if (hostRowOpen) {
      hostRowOpen(entityType, id);
      return;
    }
    if (inlineExpand) {
      const next = expandedIdRef.current === id ? null : id;
      setExpandedId(next);
      onExpandedIdChange?.(next);
      return;
    }
    void kernel.current?.focusEntityUI(entityType as EntityType, id);
  }, [entityType, hostRowOpen, inlineExpand, kernel, onExpandedIdChange]);
  const bulkSelectionEnabled = (enableSelection || bulkActions.length > 0) && Boolean(columns);
  const updateSelectedIds = useCallback((next: Set<string>) => {
    if (isSelectionControlled && onControlledSelectionChange) {
      onControlledSelectionChange(Array.from(next));
      return;
    }
    setUncontrolledSelectedIds(next);
  }, [isSelectionControlled, onControlledSelectionChange]);
  const clearSelection = useCallback(() => updateSelectedIds(new Set()), [updateSelectedIds]);

  const cleanupDragSelectionListeners = useCallback(() => {
    if (dragMouseMoveHandlerRef.current) {
      document.removeEventListener('mousemove', dragMouseMoveHandlerRef.current);
      dragMouseMoveHandlerRef.current = null;
    }
    if (dragMouseUpHandlerRef.current) {
      document.removeEventListener('mouseup', dragMouseUpHandlerRef.current);
      dragMouseUpHandlerRef.current = null;
    }
  }, []);

  const getScrollableParent = useCallback((startNode: HTMLElement | null): HTMLElement | null => {
    let cursor: HTMLElement | null = startNode;
    while (cursor) {
      const style = window.getComputedStyle(cursor);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && cursor.scrollHeight > cursor.clientHeight) {
        return cursor;
      }
      cursor = cursor.parentElement;
    }
    return document.scrollingElement instanceof HTMLElement ? document.scrollingElement : null;
  }, []);

  const handleToggleSelect = useCallback((id: string) => {
    if (suppressNextToggleRef.current) {
      suppressNextToggleRef.current = false;
      return;
    }

    updateSelectedIds(((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    })(selectedIds));
  }, [selectedIds, updateSelectedIds]);

  const handleSelectionZoneMouseDown = useCallback((event: React.MouseEvent, anchorId: string) => {
    if (!bulkSelectionEnabled) return;
    if (event.button !== 0) return;

    event.stopPropagation();

    const sourceElement = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const scrollContainer = getScrollableParent(sourceElement);
    const baselineSelectedIds = new Set(selectedIds);
    const mode: 'add' | 'remove' = baselineSelectedIds.has(anchorId) ? 'remove' : 'add';
    const orderedVisibleIds = viewItems.map((item) => item.id);

    const getRangeIds = (fromId: string, toId: string): string[] => {
      const fromIndex = orderedVisibleIds.indexOf(fromId);
      const toIndex = orderedVisibleIds.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) return [];
      const start = Math.min(fromIndex, toIndex);
      const end = Math.max(fromIndex, toIndex);
      return orderedVisibleIds.slice(start, end + 1);
    };

    const applyRangeSelection = (targetId: string) => {
      const rangeIds = getRangeIds(anchorId, targetId);
      if (rangeIds.length === 0) return;
      const nextSelection = new Set(baselineSelectedIds);
      rangeIds.forEach((id) => {
        if (mode === 'remove') {
          nextSelection.delete(id);
          return;
        }
        nextSelection.add(id);
      });
      updateSelectedIds(((prev) => {
        if (prev.size === nextSelection.size) {
          let identical = true;
          nextSelection.forEach((id) => {
            if (!prev.has(id)) identical = false;
          });
          if (identical) return prev;
        }
        return nextSelection;
      })(selectedIds));
    };

    const resolveRowIdFromPoint = (clientX: number, clientY: number): string | null => {
      const element = document.elementFromPoint(clientX, clientY);
      if (!(element instanceof HTMLElement)) return null;
      const rowHost = element.closest('[data-table-row-id]');
      if (!(rowHost instanceof HTMLElement)) return null;
      const rowId = rowHost.dataset.tableRowId;
      return typeof rowId === 'string' && rowId.length > 0 ? rowId : null;
    };

    const dragState = {
      anchorId,
      currentId: anchorId,
      baselineSelectedIds,
      mode,
      didMove: false,
      scrollContainer,
      autoScrollRaf: null as number | null,
      autoScrollSpeed: 0,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
    };
    dragSelectionRef.current = dragState;

    const stopAutoScroll = () => {
      if (dragState.autoScrollRaf !== null) {
        cancelAnimationFrame(dragState.autoScrollRaf);
        dragState.autoScrollRaf = null;
      }
      dragState.autoScrollSpeed = 0;
    };

    const updateDragTargetFromPoint = (clientX: number, clientY: number) => {
      const active = dragSelectionRef.current;
      if (!active || active !== dragState) return;
      const nextRowId = resolveRowIdFromPoint(clientX, clientY);
      if (!nextRowId || nextRowId === active.currentId) return;
      active.currentId = nextRowId;
      if (nextRowId !== active.anchorId) {
        active.didMove = true;
      }
      applyRangeSelection(nextRowId);
    };

    const autoScrollStep = () => {
      const active = dragSelectionRef.current;
      if (!active || active !== dragState || !active.scrollContainer || active.autoScrollSpeed === 0) {
        stopAutoScroll();
        return;
      }
      active.scrollContainer.scrollTop += active.autoScrollSpeed;
      updateDragTargetFromPoint(active.lastClientX, active.lastClientY);
      active.autoScrollRaf = requestAnimationFrame(autoScrollStep);
    };

    const updateAutoScroll = (clientY: number) => {
      if (!dragState.scrollContainer) {
        stopAutoScroll();
        return;
      }

      const rect = dragState.scrollContainer.getBoundingClientRect();
      const edgeThreshold = 44;
      let nextSpeed = 0;

      if (clientY < rect.top + edgeThreshold) {
        const intensity = (rect.top + edgeThreshold - clientY) / edgeThreshold;
        nextSpeed = -Math.max(2, Math.round(intensity * 16));
      } else if (clientY > rect.bottom - edgeThreshold) {
        const intensity = (clientY - (rect.bottom - edgeThreshold)) / edgeThreshold;
        nextSpeed = Math.max(2, Math.round(intensity * 16));
      }

      dragState.autoScrollSpeed = nextSpeed;
      if (nextSpeed === 0) {
        stopAutoScroll();
        return;
      }

      if (dragState.autoScrollRaf === null) {
        dragState.autoScrollRaf = requestAnimationFrame(autoScrollStep);
      }
    };

    const finishDragSelection = () => {
      cleanupDragSelectionListeners();
      stopAutoScroll();

      if (dragState.didMove) {
        applyRangeSelection(dragState.currentId);
        suppressNextToggleRef.current = true;
        window.setTimeout(() => {
          suppressNextToggleRef.current = false;
        }, 0);
      }

      if (dragSelectionRef.current === dragState) {
        dragSelectionRef.current = null;
      }
    };

    const handleDocumentMouseMove = (moveEvent: MouseEvent) => {
      const active = dragSelectionRef.current;
      if (!active || active !== dragState) return;

      active.lastClientX = moveEvent.clientX;
      active.lastClientY = moveEvent.clientY;
      updateDragTargetFromPoint(moveEvent.clientX, moveEvent.clientY);
      updateAutoScroll(moveEvent.clientY);
      moveEvent.preventDefault();
    };

    const handleDocumentMouseUp = () => {
      finishDragSelection();
    };

    dragMouseMoveHandlerRef.current = handleDocumentMouseMove;
    dragMouseUpHandlerRef.current = handleDocumentMouseUp;
    document.addEventListener('mousemove', handleDocumentMouseMove);
    document.addEventListener('mouseup', handleDocumentMouseUp, { once: true });
  }, [bulkSelectionEnabled, cleanupDragSelectionListeners, getScrollableParent, selectedIds, updateSelectedIds, viewItems]);

  useEffect(() => {
    return () => {
      cleanupDragSelectionListeners();
      const activeDrag = dragSelectionRef.current;
      if (activeDrag && activeDrag.autoScrollRaf !== null) {
        cancelAnimationFrame(activeDrag.autoScrollRaf);
      }
      dragSelectionRef.current = null;
    };
  }, [cleanupDragSelectionListeners]);

  useEffect(() => {
    if (!bulkSelectionEnabled && selectedIds.size > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      clearSelection();
    }
    if (!bulkSelectionEnabled) {
      cleanupDragSelectionListeners();
      const activeDrag = dragSelectionRef.current;
      if (activeDrag && activeDrag.autoScrollRaf !== null) {
        cancelAnimationFrame(activeDrag.autoScrollRaf);
      }
      dragSelectionRef.current = null;
    }
  }, [bulkSelectionEnabled, selectedIds.size, clearSelection, cleanupDragSelectionListeners]);

  useEffect(() => {
    if (isSelectionControlled || selectedIds.size === 0) return;
    const visibleIdSet = new Set(viewItems.map((item) => item.id));
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setUncontrolledSelectedIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIdSet.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [isSelectionControlled, viewItems, selectedIds.size]);

  const assetFilterOptions = useMemo<TableFilterOption<K>[]>(() => filterOptions || [
    { key: 'name', label: 'Asset', icon: <Type size={16} strokeWidth={1.9} /> },
    { key: 'country', label: 'Country', icon: <MapPin size={16} strokeWidth={1.9} /> },
    { key: 'assetType', label: 'Asset type', icon: <Type size={16} strokeWidth={1.9} /> },
    { key: 'owner', label: 'Owner', icon: <Users size={16} strokeWidth={1.9} /> },
  ] as TableFilterOption<K>[], [filterOptions]);

  const currentSortLabel = useMemo(
    () => sortOptions.find((o) => o.field === typedViewFilters.sortBy)?.label || 'Sort',
    [sortOptions, typedViewFilters.sortBy]
  );

  const visibleSelectedCount = useMemo(
    () => viewItems.reduce((count, item) => count + (selectedIds.has(item.id) ? 1 : 0), 0),
    [viewItems, selectedIds],
  );
  const allVisibleSelected = viewItems.length > 0 && visibleSelectedCount === viewItems.length;
  const partiallySelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const isColumnHidden = useCallback(
    (c: TableColumnDef) => (columnVisibility[c.id] === undefined ? Boolean(c.defaultHidden) : !columnVisibility[c.id]),
    [columnVisibility],
  );

  const activeColumns = useMemo(() => {
    if (!columns) return undefined;
    const visible = columns.filter((c) => !isColumnHidden(c));
    if (!bulkSelectionEnabled) return visible;

    return [
      {
        id: '_selection',
        minWidth: 44,
        maxWidth: 52,
        align: 'center' as const,
        header: (
          <TableSelectionCheckbox
            checked={allVisibleSelected}
            indeterminate={partiallySelected}
            disabled={viewItems.length === 0}
            onChange={() => {
              if (allVisibleSelected) {
                clearSelection();
                return;
              }
              updateSelectedIds(new Set(viewItems.map((item) => item.id)));
            }}
          />
        ),
      },
      ...visible,
    ];
  }, [
    columns,
    isColumnHidden,
    bulkSelectionEnabled,
    allVisibleSelected,
    partiallySelected,
    viewItems,
    clearSelection,
    updateSelectedIds,
  ]);

  const hideableColumns = useMemo(
    // '_'-prefixed columns are the table's own controls (selection, expand handles), not data.
    () => (columns ?? []).filter((c) => c.id !== 'actions' && !c.id.startsWith('_')),
    [columns],
  );
  const setColumnsVisible = useCallback((colIds: string[], visible: boolean) => {
    setColumnVisibility((prev) => {
      const next = { ...prev };
      for (const id of colIds) next[id] = visible;
      return next;
    });
  }, []);

  const autoSizeColumn = useCallback(
    (colId: string) => {
      const colIdx = activeColumns?.findIndex((c) => c.id === colId) ?? -1;
      autoSizeAt(colId, tableContainerRef.current, colIdx, activeColumns?.length ?? 0);
    },
    [activeColumns, autoSizeAt],
  );
  
  const renderAssetSortIcon = useCallback((label: string) => {
    const normalized = label.toLowerCase();
    if (normalized.includes('alpha')) return <Type size={16} strokeWidth={1.9} />;
    if (normalized.includes('city')) return <MapPin size={16} strokeWidth={1.9} />;
    if (normalized.includes('unit') || normalized.includes('count')) return <Hash size={16} strokeWidth={1.9} />;
    if (normalized.includes('owner')) return <Users size={16} strokeWidth={1.9} />;
    if (normalized.includes('occupancy')) return <Gauge size={16} strokeWidth={1.9} />;
    return <ArrowUpDown size={16} strokeWidth={1.9} />;
  }, []);

  const selectedEntities = useMemo(
    () => viewItems.filter((item) => selectedIds.has(item.id)),
    [viewItems, selectedIds],
  );

  const statsEntity = useMemo<InventoryStatsEntityType | null>(() => {
    if (!supportsStats) return null;
    if (statsEntityType) return statsEntityType;
    if (entityType === 'Audit' || entityType === 'Brand') {
      return entityType;
    }
    return null;
  }, [entityType, statsEntityType, supportsStats]);

  const viewFiltersOrganisationId = typedViewFilters.organisationId;
  const normalizedFilterOrganisationId = useMemo(() => {
    if (typeof viewFiltersOrganisationId !== 'string') return '';
    return viewFiltersOrganisationId.trim();
  }, [viewFiltersOrganisationId]);

  const statsOrganisationId = useMemo(() => {
    if (forceGlobalStats || statsEntity === 'Brand') {
      return undefined;
    }
    if (normalizedFilterOrganisationId) return normalizedFilterOrganisationId;
    if (userContext.isFaterAdmin) return undefined;
    return typeof userContext.organisationId === 'string'
      ? userContext.organisationId
      : undefined;
  }, [forceGlobalStats, normalizedFilterOrganisationId, statsEntity, userContext.isFaterAdmin, userContext.organisationId]);

  const statsScopeKey = useMemo(() => {
    const normalizedOrganisationId = typeof statsOrganisationId === 'string'
      ? statsOrganisationId.trim()
      : '';
    return normalizedOrganisationId || INVENTORY_STATS_GLOBAL_SCOPE_KEY;
  }, [statsOrganisationId]);

  const statsEntry = useKernelStore((state) => {
    if (!statsEntity) return null;
    const entityStats = state.inventoryStats[statsEntity] ?? {};
    return entityStats[statsScopeKey] ?? null;
  });

  const loadStats = useCallback((force = false) => {
    if (!statsEntity) return;
    void kernel.current?.fetchInventoryStats(statsEntity, {
      force,
      organisationId: statsOrganisationId,
    }).catch(() => undefined);
  }, [kernel, statsEntity, statsOrganisationId]);

  useEffect(() => {
    if (viewMode !== 'stats' || !statsEntity) return;
    if (!statsEntry?.data && !statsEntry?.isLoading) {
      loadStats(false);
      return;
    }
    if (statsEntry?.stale && !statsEntry?.isLoading) {
      loadStats(true);
    }
  }, [loadStats, statsEntity, statsEntry?.data, statsEntry?.isLoading, statsEntry?.stale, viewMode]);


  const resolveContextSelection = useCallback((item: InventoryEntityState<K>) => {
    if (selectedIds.size === 0) return [item];
    if (selectedIds.has(item.id)) return selectedEntities;

    const merged = new Map<string, InventoryEntityState<K>>();
    selectedEntities.forEach((entity) => merged.set(entity.id, entity));
    merged.set(item.id, item);
    return Array.from(merged.values());
  }, [selectedEntities, selectedIds]);

  const handleToolbarContextMenu = useCallback((event: React.MouseEvent) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    if (target?.closest('[data-table-row-id]')) return;

    const actions = (toolbarContextMenuActions || []).filter((action) => !action.disabled);
    if (actions.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    kernel.current?.setMenuData({
      position: { x: event.clientX, y: event.clientY },
      actions: actions.map((action) => ({
        label: action.label,
        icon: React.isValidElement(action.icon) ? action.icon : undefined,
        onClick: action.onClick,
      })),
    });
  }, [kernel, toolbarContextMenuActions]);

  const handleRowContextMenu = useCallback((event: React.MouseEvent, item: InventoryEntityState<K>) => {
    const visibleRowActions = rowActions
      .filter((action) => action.visible?.(item) !== false)
      .filter((action) => !action.disabled?.(item));
    const isStatusAction = (actionId: string) => /interesting|dismiss|discard/i.test(actionId);
    const statusRowActions = visibleRowActions.filter((action) => isStatusAction(action.id));
    const regularRowActions = visibleRowActions.filter((action) => !isStatusAction(action.id));
    const mapRowAction = (action: EntityRowAction<InventoryEntityState<K>>): QuickMenuAction => ({
      label: action.label,
      icon: React.isValidElement(action.icon) ? action.icon : undefined,
      onClick: () => void action.onClick(item),
    });

    const rowMenuActions: QuickMenuAction[] = [
      ...regularRowActions.map(mapRowAction),
      ...(statusRowActions.length > 0
        ? [{
            label: lang === 'fr' ? "Changer d'état" : 'Change status',
            onClick: () => undefined,
            subActions: statusRowActions.map(mapRowAction),
          }]
        : []),
    ];

    const contextSelection = bulkSelectionEnabled && bulkActions.length > 0
      ? resolveContextSelection(item)
      : [];
    const bulkMenuActions: QuickMenuAction[] = contextSelection.length > 0
      ? bulkActions
          .filter((action) => !action.disabled?.(contextSelection))
          .map((action) => ({
            label: `${action.label} (${contextSelection.length})`,
            icon: React.isValidElement(action.icon) ? action.icon : undefined,
            onClick: () => void action.onClick(contextSelection, clearSelection),
          }))
      : [];

    const actions = [...rowMenuActions, ...bulkMenuActions];
    if (actions.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    if (contextSelection.length > 0) {
      // Keep UI selection aligned with context-menu target set for bulk actions.
      updateSelectedIds(((prev) => {
        const next = new Set(prev);
        contextSelection.forEach((entity) => next.add(entity.id));
        return next;
      })(selectedIds));
    }

    kernel.current?.setMenuData({
      position: { x: event.clientX, y: event.clientY },
      actions,
    });
  }, [bulkActions, bulkSelectionEnabled, clearSelection, kernel, lang, resolveContextSelection, rowActions, selectedIds, updateSelectedIds]);

  const { totalMinWidth, gridTemplateColumns } = useMemo(() => {
    if (!activeColumns) return { totalMinWidth: 0, gridTemplateColumns: '' };
    return {
      totalMinWidth: activeColumns.reduce((sum, c) => sum + c.minWidth, 0),
      gridTemplateColumns: activeColumns
        // eslint-disable-next-line react-hooks/refs -- pre-existing ref access through the kernel handle, which is a ref container by design
        .map((c) => {
          const override = columnWidthsRef.current[c.id];
          if (override) return `${override}fr`;
          return typeof c.maxWidth === 'number' && Number.isFinite(c.maxWidth) && c.maxWidth >= c.minWidth
            ? `minmax(${c.minWidth}px, ${c.maxWidth}px)`
            : `${c.minWidth}fr`;
        })
        .join(' '),
    };
    // widthVersion included so re-computation happens when the mutable ref changes.
  }, [activeColumns, widthVersion]);

  // Glass is the default table surface app-wide; pass glass={false} to opt out.
  const effectiveGlass = glass !== false;

  return (
    <TableGlassProvider value={effectiveGlass}>
    <ViewSection
      wrapperClassName={sheet ? 'h-full min-h-0' : (hasLockedFilters || preview) ? 'h-auto min-h-0' : undefined}
      contentClassName={sheet ? '!px-0 !py-0 !space-y-0 flex h-full min-h-0 flex-col' : '!px-0 !py-0 !space-y-0'}
      onReachBottom={viewMode === 'rows' && !preview ? handleReachBottom : undefined}
      hideViewLabel={true}
      useAbsoluteScrollContainer={sheet || (!hasLockedFilters && !preview)}
    >
      {!preview && !hideToolbar && (
      <div onContextMenu={handleToolbarContextMenu}>
      <InventoryListToolbar
        title={pageTitle}
        beforeCreateContent={beforeCreateContent}
        onCreateClick={onCreateClick}
        createButtonLabel={createButtonLabel}
        searchValue={String(typedViewFilters.search || '')}
        hideSearch={hideSearch}
        onSearchChange={handleSearchChange}
        disableManualRefresh={disableManualRefresh}
        refreshIsLoading={refreshIsLoading}
        onManualRefresh={handleManualRefresh}
        beforeFilterContent={
          supportsStats && statsEntity ? (
            <button
              type="button"
              onClick={() => setViewMode((current) => (current === 'rows' ? 'stats' : 'rows'))}
              className={clsx(
                'inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                viewMode === 'stats'
                  ? 'bg-white/[0.12] text-white'
                  : 'text-white/75 hover:bg-white/[0.08] hover:text-white'
              )}
              title={viewMode === 'stats' ? 'Show rows' : 'Show stats'}
              aria-label={viewMode === 'stats' ? 'Show rows' : 'Show stats'}
            >
              <BarChart3 size={15} strokeWidth={2.1} />
            </button>
          ) : null
        }
        filterOptions={assetFilterOptions}
        viewFilters={viewFilters as Record<string, unknown>}
        isFilterKeyLocked={isFilterKeyLocked}
        mergeWithLockedFilters={mergeWithLockedFilters}
        setFilters={setFilters}
        sortOptions={sortOptions}
        activeSortField={String(typedViewFilters.sortBy || '')}
        activeSortDir={String(typedViewFilters.sortDir || 'desc')}
        onSortChange={handleSortChange}
        currentSortLabel={currentSortLabel}
        renderSortIcon={renderAssetSortIcon}
        afterSortContent={
          hideableColumns.length > 0 ? (
            <div className="relative" data-table-cols-menu>
              <AppTooltip content="Columns">
              <button
                type="button"
                aria-label="Columns"
                onClick={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect();
                  setColumnsMenuAnchor({ left: rect.left, top: rect.bottom + 8 });
                  setColumnsMenuOpen((value) => !value);
                }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#E5E5E5] transition-colors hover:bg-white/[0.08] hover:text-white"
              >
                <SlidersHorizontal size={17} strokeWidth={2} />
              </button>
              </AppTooltip>
              {columnsMenuOpen && columnsMenuAnchor && typeof document !== 'undefined' && createPortal(
                <div
                  data-table-cols-menu
                  className="fixed z-[1000] max-h-[60vh] w-[300px] overflow-y-auto rounded-[20px] border border-white/[0.06] bg-[#353535] p-1.5 shadow-[0_18px_52px_rgba(0,0,0,0.5)]"
                  style={{ left: columnsMenuAnchor.left, top: columnsMenuAnchor.top }}
                >
                  {/* Notion's property visibility (owner, 2026-09-28): search, what is shown, what is
                      hidden, all-at-once switches, an eye per column. */}
                  <div className="flex items-center justify-between px-2.5 pb-2 pt-1">
                    <span className="font-medium text-white" style={{ fontSize: 14 }}>Columns</span>
                    <button
                      type="button"
                      aria-label="Close"
                      onClick={() => setColumnsMenuOpen(false)}
                      className="flex h-6 w-6 items-center justify-center rounded-full bg-white/[0.08] text-[#B4B4B4] transition-colors hover:text-white"
                    >
                      <X size={13} strokeWidth={2.4} />
                    </button>
                  </div>
                  <input
                    autoFocus
                    value={columnsQuery}
                    onChange={(event) => setColumnsQuery(event.target.value)}
                    placeholder="Search for a column…"
                    className="mx-1 mb-2 h-8 w-[calc(100%-8px)] rounded-full border border-white/[0.08] bg-transparent px-3 text-white outline-none placeholder:text-[#AFAFAF] focus:border-[#3862C2]"
                    style={{ fontSize: 13 }}
                  />
                  {([false, true] as const).map((hiddenGroup) => {
                    const needle = columnsQuery.trim().toLowerCase();
                    const group = hideableColumns.filter((col) => isColumnHidden(col) === hiddenGroup);
                    const nameOf = (col: TableColumnDef) => col.label ?? (typeof col.header === 'string' && col.header.trim() ? col.header : col.id);
                    const rows = needle ? group.filter((col) => nameOf(col).toLowerCase().includes(needle)) : group;
                    if (group.length === 0) return null;
                    return (
                      <div key={String(hiddenGroup)} className="mb-1">
                        <div className="flex items-center justify-between px-2.5 py-1">
                          <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>{hiddenGroup ? 'Hidden in table' : 'Shown in table'}</span>
                          <button
                            type="button"
                            onClick={() => setColumnsVisible(group.map((col) => col.id), hiddenGroup)}
                            className="text-[#7FA4F0] transition-colors hover:text-[#A9C2F5]"
                            style={{ fontSize: 12 }}
                          >
                            {hiddenGroup ? 'Show all' : 'Hide all'}
                          </button>
                        </div>
                        {rows.map((col) => (
                          <button
                            key={col.id}
                            type="button"
                            onClick={() => setColumnsVisible([col.id], hiddenGroup)}
                            className={clsx('flex h-9 w-full items-center gap-2.5 rounded-[12px] px-2.5 text-left transition-colors hover:bg-white/[0.08]', hiddenGroup ? 'text-[#8E8E8E]' : 'text-white')}
                          >
                            <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14 }}>{nameOf(col)}</span>
                            {hiddenGroup
                              ? <EyeOff size={15} strokeWidth={2} className="shrink-0 text-[#6E6E6E]" />
                              : <Eye size={15} strokeWidth={2} className="shrink-0 text-[#B4B4B4]" />}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>,
                document.body,
              )}
            </div>
          ) : null
        }
        rightToolbarContent={renderToolbarExtras ? renderToolbarExtras() : null}
      />
      </div>
      )}

      {/* Summary metrics on top of the table (main list views only, not embedded
          scoped sub-lists inside a detail panel, and not in a compact preview). */}
      {!hasLockedFilters && !preview && (
        <InventoryMetricsBar entityType={entityType} items={viewItems} total={viewPagination?.total ?? null} aggregations={viewPagination?.aggregations ?? null} />
      )}

      <div className={clsx(sheet ? 'min-h-0 flex-1 flex flex-col' : (hasLockedFilters || preview) ? 'flex flex-col min-h-0' : 'flex-1 flex flex-col min-h-[200px]', 'relative')}>
        <div ref={tableContainerRef} onContextMenu={handleToolbarContextMenu} className={clsx(activeColumns ? styles.tableContainer : 'w-full flex flex-col overflow-hidden rounded-none border border-[#2A2A2A] bg-[#181818]', pageTitle && styles.pageInset, effectiveGlass && TABLE_GLASS_CONTAINER, effectiveGlass && styles.glassSurface, sheet && styles.sheetSurface)} style={activeColumns ? { '--table-min-width': `${totalMinWidth}px`, '--table-grid-cols': gridTemplateColumns } as React.CSSProperties : undefined}>

          {viewMode === 'stats' ? (
            <div className="min-h-[260px]">
              {renderStatsView
                ? renderStatsView(statsEntry, {
                    onRetry: () => loadStats(true),
                    entityType,
                    scopeKey: statsScopeKey,
                  })
                : <TableStatsPanel statsEntry={statsEntry} onRetry={() => loadStats(true)} />}
            </div>
          ) : (
            <>
              {viewError && (
                <div role="alert" className="p-6 text-sm text-amber-200 flex items-center gap-3">
                  <span>{lang === 'fr' ? 'Impossible de charger les résultats.' : 'Unable to load results.'}{viewItems.length > 0 ? (lang === 'fr' ? ' Les résultats précédents sont affichés et peuvent être périmés.' : ' Previously loaded results are shown and may be out of date.') : ''}</span>
                  <button type="button" onClick={handleManualRefresh} disabled={refreshIsLoading} className="underline disabled:opacity-50">{lang === 'fr' ? 'Réessayer' : 'Retry'}</button>
                </div>
              )}
              <div onContextMenu={handleToolbarContextMenu} className={activeColumns ? clsx(styles.gridRow, styles.headerRow, effectiveGlass && !sheet && TABLE_GLASS_HEADER) : clsx('sticky top-0 z-10', gridClass, 'h-10 bg-[#1D1D1D] border-b border-[#2A2A2A] text-[13px] font-normal normal-case tracking-normal text-white/55 backdrop-blur-none', effectiveGlass && !sheet && TABLE_GLASS_HEADER)} style={{ fontFamily: 'ui-sans-serif, -apple-system, system-ui, sans-serif' }}>
                {activeColumns ? activeColumns.map((column, idx) => {
                  const isResizable = column.id !== '_selection' && idx < activeColumns.length - 1;
                  return (
                    <div
                      key={column.id}
                      className={clsx(styles.cell, 'relative')}
                      style={{
                        justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start',
                        padding: column.id === '_selection' ? '0 8px' : undefined,
                      }}
                    >
                      <ColumnHeaderLabel column={column} plain={effectiveGlass} />
                      {isResizable && (
                        <div
                          role="separator"
                          aria-orientation="vertical"
                          title="Drag to resize. Double-click to auto-size."
                          className="absolute right-0 top-0 z-20 h-full w-2 cursor-col-resize hover:bg-white/10"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const startWidth =
                              columnWidthsRef.current[column.id] ?? column.minWidth;
                            startColumnDrag(column.id, e.clientX, startWidth);
                          }}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            autoSizeColumn(column.id);
                          }}
                        />
                      )}
                    </div>
                  );
                }) : renderTableHeader?.()}
              </div>

              <div
                ref={rowsContainerRef}
                onDoubleClick={handleCellDoubleClick}
                aria-busy={rescoreActive || undefined}
                className={clsx(hasLockedFilters ? '' : 'flex-1', rescoreActive && 'shortlist-rescoring')}
              >
                {viewItems.map((item) => (
                  <TableRowWrapper
                    key={item.id}
                    item={item}
                    renderRow={renderRow}
                    columns={activeColumns}
                    isExpanded={inlineExpand && expandedId === item.id}
                    onToggle={handleToggleRow}
                    onContextMenu={handleRowContextMenu}
                    isSelectable={bulkSelectionEnabled}
                    isSelected={selectedIds.has(item.id)}
                    onToggleSelect={handleToggleSelect}
                    onSelectionZoneMouseDown={handleSelectionZoneMouseDown}
                    onOpen={hostRowOpen ? handleToggleRow : undefined}
                  />
                ))}
                
                {/* Trailing rows, INSIDE the scroll container and after the last real one. A
                    caller that renders this as a sibling of <Table> instead gets it pinned under a
                    full-height, internally-scrolling table - which is how the sourcing "brands
                    being analysed" strip ended up permanently at the bottom of the viewport and
                    drawn over the rows it was supposed to follow. Only shown when there ARE rows:
                    with none, emptyStateContent owns the surface and already explains the wait. */}
                {viewItems.length > 0 &&
                  (typeof afterRowsContent === 'function' ? afterRowsContent(activeColumns) : afterRowsContent)}

                {viewIsLoading && viewItems.length === 0 && <SkeletonRows />}

                {!viewError && !viewIsLoading && viewItems.length === 0 && (
                  emptyStateContent ?? (
                    <div className="p-12 text-center text-white/20 text-sm italic">No results found.</div>
                  )
                )}
              </div>
            </>
          )}
        </div>

      </div>
    </ViewSection>
    {cellViewer && typeof document !== 'undefined' && createPortal(
      <>
        <div className="fixed inset-0 z-[1000]" onMouseDown={() => setCellViewer(null)} />
        <div ref={cellCardRef} role="dialog" aria-label="Cell content" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
          className="custom-scrollbar fixed z-[1001] flex flex-col gap-2 overflow-y-auto rounded-[12px] border border-white/[0.06] bg-[#353535] px-4 py-2.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)]"
          style={{ top: cellViewer.top, left: cellViewer.left, width: cellViewer.width, maxHeight: cellViewer.maxHeight, animation: 'fadeIn 140ms ease-out' }}>
          {cellViewer.crop && <PageCrop {...cellViewer.crop} width={cellViewer.width - 32} height={Math.round((cellViewer.width - 32) * 0.62)} span={0.5} />}
          {cellViewer.text && <div className="whitespace-pre-wrap break-words text-[13px] leading-[1.45] text-[#E8E8E8]">{cellViewer.text}</div>}
        </div>
      </>, document.body)}
    </TableGlassProvider>
  );
}

export const Table = memo(TableInner) as typeof TableInner;


// Campaign-table review mode helper. Kept here to avoid a separate campaign source file for a tiny table-only extension.
type ReviewSnapshot = {
  campaignType: string;
  campaignId: string;
  entityType: string;
  entityId: string;
  brandName: string;
  score: number | string;
  initialStatus: string;
  unitName?: string;
  unitIdentifier?: string;
};

interface UseCampaignTableReviewModeOptions<K extends InventoryEntityType> {
  campaignType: string;
  campaignId: string;
  entityType: K;
  reviewFilters: Partial<InventoryRegistry[K]['filters']>;
  filePrefix: string;
  includeUnitColumns?: boolean;
  buildSnapshot: (item: InventoryEntityState<K>) => Omit<ReviewSnapshot, 'campaignType' | 'campaignId' | 'entityType' | 'entityId'>;
}

const labels = {
  en: {
    enter: 'Enter review mode',
    active: 'Review mode',
    reroll: 'Reroll',
    exportCsv: 'Export CSV',
    exit: 'Exit',
    loadFailed: 'Failed to load review sample.',
    empty: 'No campaign results found for review.',
    exportFailed: 'Failed to export review CSV.',
    exportSuccess: (count: number) => `${count} review row(s) exported.`,
  },
  fr: {
    enter: 'Activer le mode review',
    active: 'Review mode',
    reroll: 'Reroll',
    exportCsv: 'Export CSV',
    exit: 'Quitter',
    loadFailed: "Echec du chargement de l'echantillon review.",
    empty: 'Aucun resultat de campagne a revoir.',
    exportFailed: "Echec de l'export CSV review.",
    exportSuccess: (count: number) => `${count} ligne(s) review exportee(s).`,
  },
} as const;

const readStatus = (item: unknown): string => {
  if (!item || typeof item !== 'object') return '';
  const status = (item as { status?: unknown }).status;
  return typeof status === 'string' ? status : '';
};

export function useCampaignTableReviewMode<K extends InventoryEntityType>({
  campaignType,
  campaignId,
  entityType,
  reviewFilters,
  filePrefix,
  includeUnitColumns = false,
  buildSnapshot,
}: UseCampaignTableReviewModeOptions<K>) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = labels[lang];
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reviewIds, setReviewIds] = useState<string[]>([]);
  const [snapshotById, setSnapshotById] = useState<Record<string, ReviewSnapshot>>({});
  const reviewRequestSeqRef = useRef(0);

  useEffect(() => {
    reviewRequestSeqRef.current += 1;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setIsActive(false);
    setIsLoading(false);
    setReviewIds([]);
    setSnapshotById({});
  }, [campaignId, entityType]);

  const loadReviewSample = useCallback(async () => {
    if (!campaignId || !kernel.current) return;
    const requestSeq = reviewRequestSeqRef.current + 1;
    reviewRequestSeqRef.current = requestSeq;
    const activeCampaignId = campaignId;
    setIsLoading(true);
    try {
      const result = await kernel.current.queryInventory(entityType, {
        filters: { ...reviewFilters, reviewMode: true } as Partial<InventoryRegistry[K]['filters']>,
        limit: 30,
        offset: 0,
        force: true,
        ttl: 0,
      });
      if (requestSeq !== reviewRequestSeqRef.current) return;
      if (result.error) throw new Error(result.error);

      const inventory = kernel.current?.getInventory();
      const registry = inventory?.[entityType]?.registry as Record<string, InventoryEntityState<K> | undefined> | undefined;
      const nextIds = result.ids.filter((id) => Boolean(registry?.[id]));
      const nextSnapshots = nextIds.reduce<Record<string, ReviewSnapshot>>((acc, id) => {
        const item = registry?.[id];
        if (!item) return acc;
        const snapshot = buildSnapshot(item);
        acc[id] = {
          campaignType,
          campaignId: activeCampaignId,
          entityType,
          entityId: id,
          ...snapshot,
        };
        return acc;
      }, {});

      setReviewIds(nextIds);
      setSnapshotById(nextSnapshots);
      setIsActive(true);
      if (nextIds.length === 0) toast.info(t.empty);
    } catch (error) {
      if (requestSeq !== reviewRequestSeqRef.current) return;
      console.error('[CampaignReviewMode] Failed to load sample', error);
      toast.error(t.loadFailed);
    } finally {
      if (requestSeq === reviewRequestSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [buildSnapshot, campaignId, campaignType, entityType, kernel, reviewFilters, t.empty, t.loadFailed]);

  const exitReviewMode = useCallback(() => {
    reviewRequestSeqRef.current += 1;
    setIsActive(false);
    setIsLoading(false);
    setReviewIds([]);
    setSnapshotById({});
  }, []);

  const exportReviewCsv = useCallback(() => {
    try {
      const inventory = kernel.current?.getInventory();
      const registry = inventory?.[entityType]?.registry as Record<string, unknown> | undefined;
      const headers = [
        'campaignType',
        'campaignId',
        'entityType',
        'entityId',
        'brandName',
        'score',
        'initialStatus',
        'finalStatus',
        'statusChanged',
        ...(includeUnitColumns ? ['unitName', 'unitIdentifier'] : []),
      ];
      const rows = reviewIds.map((id) => {
        const snapshot = snapshotById[id];
        const finalStatus = readStatus(registry?.[id]) || snapshot?.initialStatus || '';
        return [
          snapshot?.campaignType ?? campaignType,
          snapshot?.campaignId ?? campaignId,
          snapshot?.entityType ?? entityType,
          snapshot?.entityId ?? id,
          snapshot?.brandName ?? '',
          snapshot?.score ?? '',
          snapshot?.initialStatus ?? '',
          finalStatus,
          String((snapshot?.initialStatus ?? '') !== finalStatus),
          ...(includeUnitColumns ? [snapshot?.unitName ?? '', snapshot?.unitIdentifier ?? ''] : []),
        ];
      });
      downloadCsv(`${filePrefix}_${campaignId}_review`, buildCsvContent(headers, rows));
      toast.success(t.exportSuccess(rows.length));
    } catch (error) {
      console.error('[CampaignReviewMode] CSV export failed', error);
      toast.error(t.exportFailed);
    }
  }, [campaignId, campaignType, entityType, filePrefix, includeUnitColumns, kernel, reviewIds, snapshotById, t]);

  const contextMenuActions = useMemo<TableContextMenuAction[]>(() => {
    if (isActive || !campaignId) return [];
    return [
      {
        label: t.enter,
        disabled: isLoading,
        onClick: () => void loadReviewSample(),
      },
    ];
  }, [campaignId, isActive, isLoading, loadReviewSample, t.enter]);

  const controls = isActive ? (
    <div className="ml-1 inline-flex h-8 items-center gap-1 rounded-[10px] border border-white/10 bg-white/[0.04] px-1.5 text-[12px] text-white/75">
      <span className="px-2 font-medium text-white/85">{t.active}</span>
      <button
        type="button"
        onClick={() => void loadReviewSample()}
        disabled={isLoading}
        className="rounded-[8px] px-2 py-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isLoading ? '...' : t.reroll}
      </button>
      <button
        type="button"
        onClick={exportReviewCsv}
        className="rounded-[8px] px-2 py-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
      >
        {t.exportCsv}
      </button>
      <button
        type="button"
        onClick={exitReviewMode}
        className="rounded-[8px] px-2 py-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
      >
        {t.exit}
      </button>
    </div>
  ) : null;

  return {
    isActive,
    reviewIds,
    contextMenuActions,
    controls,
  };
}
